import Bluebird from "bluebird";
import * as path from "path";
import * as winapi from "winapi-bindings";
import { getGameStores } from "../extensions/gamemode_management/util/getGame";
import { makeExeId } from "../reducers/session";
import { GameEntryNotFound, GameStoreNotFound } from "../types/IGameStore";
import { ProcessCanceled, UserCanceled } from "./CustomErrors";
import * as fs from "./fs";
import getNormalizeFunc from "./getNormalizeFunc";
import { log } from "./log";
import { toBlue } from "./util";
export const defaultPriority = 100;
class GameStoreHelper {
    mApi;
    mStores;
    mStoresDict;
    // Search for a specific game store.
    getGameStore(storeId) {
        const gameStores = this.getStores();
        const gameStore = gameStores.find((store) => store.id === storeId);
        if (gameStores.length > 0 && gameStore === undefined) {
            // The game stores are guaranteed to have loaded at this point,
            //  yet the store Id we're looking for is not in the store array.
            throw new GameStoreNotFound(storeId);
        }
        return gameStore;
    }
    // Returns the id of the first game store that has
    //  an existing game entry for the game we're looking for.
    //  Will return undefined if no store has a matching game entry.
    // OR
    // If a store id is specified, it will return the provided
    //  store id if the game is installed using the specified store id;
    //  otherwise will return undefined.
    isGameInstalled(id, storeId) {
        return (storeId !== undefined
            ? this.findGameEntry("id", id, storeId)
            : this.findGameEntry("id", id))
            .then((entry) => Bluebird.resolve(entry?.gameStoreId))
            .catch(() => Bluebird.resolve(undefined));
    }
    isGameStoreInstalled(storeId) {
        try {
            const gameStore = this.getGameStore(storeId);
            return gameStore?.isGameStoreInstalled
                ? gameStore.isGameStoreInstalled()
                : (gameStore
                    ?.getGameStorePath()
                    .then((execPath) => execPath === undefined
                    ? Bluebird.reject(new Error(`failed to determine path for ${storeId}`))
                    : fs.statAsync(execPath))
                    .then(() => Bluebird.resolve(true))
                    .catch((err) => {
                    log("debug", "gamestore is not installed", err);
                    return Bluebird.resolve(false);
                }) ?? Bluebird.resolve(false));
        }
        catch (err) {
            return Bluebird.resolve(false);
        }
    }
    registryLookup(lookup) {
        if (lookup === undefined) {
            return Bluebird.reject(new Error("invalid store query, provide an id!"));
        }
        const chunked = lookup.split(":", 3);
        if (chunked.length !== 3) {
            return Bluebird.reject(new Error("invalid query, should be hive:path:key"));
        }
        if (![
            "HKEY_CLASSES_ROOT",
            "HKEY_CURRENT_CONFIG",
            "HKEY_CURRENT_USER",
            "HKEY_LOCAL_MACHINE",
            "HKEY_USERS",
        ].includes(chunked[0])) {
            return Bluebird.reject(new Error("invalid query, hive should be something like HKEY_LOCAL_MACHINE"));
        }
        try {
            const instPath = winapi.RegGetValue(chunked[0], chunked[1], chunked[2]);
            if (!instPath || instPath.type !== "REG_SZ") {
                throw new Error("empty or invalid registry key");
            }
            const result = {
                appid: lookup,
                gamePath: instPath.value,
                gameStoreId: "registry",
                name: path.basename(instPath.value),
                priority: defaultPriority,
            };
            return Bluebird.resolve(result);
        }
        catch (err) {
            return Bluebird.reject(new GameEntryNotFound(lookup, "registry"));
        }
    }
    find = toBlue(async (query) => {
        const results = [];
        for (const storeId of Object.keys(query)) {
            let prioOffset = 0;
            for (const storeQuery of query[storeId]) {
                let result = undefined;
                try {
                    if (storeId === "registry") {
                        result = await this.registryLookup(storeQuery.id);
                    }
                    else if (storeQuery.id !== undefined) {
                        result = await this.findGameEntry("id", storeQuery.id, storeId);
                    }
                    else if (storeQuery.name !== undefined) {
                        result = await this.findGameEntry("name", storeQuery.name, storeId);
                    }
                    else {
                        throw new Error("invalid store query, set either id or name");
                    }
                }
                catch (err) {
                    if (!(err instanceof GameEntryNotFound)) {
                        log("error", "Failed to look up game", {
                            storeId,
                            appid: storeQuery.id,
                            name: storeQuery.name,
                        });
                    }
                }
                if (result) {
                    result.priority =
                        storeQuery.prefer ??
                            this.mStoresDict[result.gameStoreId]?.priority ??
                            defaultPriority;
                    result.priority += prioOffset++ / 1000;
                    results.push(result);
                }
            }
        }
        return results;
    });
    findByName(name, storeId) {
        return this.validInput(name)
            ? this.findGameEntry("name", name, storeId)
            : Bluebird.reject(new GameEntryNotFound("Invalid name input", this.mStores.map((store) => store.id).join(", ")));
    }
    findByAppId(appId, storeId) {
        return this.validInput(appId)
            ? this.findGameEntry("id", appId, storeId)
            : Bluebird.reject(new GameEntryNotFound("Invalid appId input", this.mStores.map((store) => store.id).join(", ")));
    }
    launchGameStore(api, gameStoreId, parameters, askConsent = false) {
        let gameStore;
        try {
            gameStore = this.getGameStore(gameStoreId);
            if (!gameStore?.getGameStorePath) {
                throw new ProcessCanceled("gamestore implementation does not define getGameStorePath");
            }
        }
        catch (err) {
            api.showErrorNotification?.("Failed to launch game store", err);
            return Bluebird.resolve();
        }
        const t = api.translate;
        const launchStore = () => this.isGameStoreInstalled(gameStoreId).then((gamestoreInstalled) => {
            if (!gamestoreInstalled) {
                api.showErrorNotification?.("Game store is not installed", t("Please install/reinstall {{storeId}} to be able to launch this game store.", { replace: { storeId: gameStoreId } }), { allowReport: false });
                return Bluebird.resolve();
            }
            // Game Store specific launch has priority.
            if (gameStore.launchGameStore) {
                return gameStore.launchGameStore(api, parameters).catch((err) => {
                    api.showErrorNotification?.("Failed to launch game store", err);
                    return Bluebird.resolve();
                });
            }
            return gameStore.getGameStorePath().then((launcherPath) => {
                if (!!launcherPath && !this.isStoreRunning(launcherPath)) {
                    api.runExecutable(launcherPath, parameters || [], {
                        detach: true,
                        suggestDeploy: false,
                    });
                }
                return Bluebird.resolve();
            });
        });
        const isGameStoreRunning = () => gameStore.getGameStorePath
            ? gameStore
                .getGameStorePath()
                .then((launcherPath) => !!launcherPath && this.isStoreRunning(launcherPath))
            : Bluebird.resolve(false);
        const askConsentDialog = () => {
            return isGameStoreRunning().then((res) => res
                ? Bluebird.resolve()
                : new Bluebird((resolve, reject) => {
                    api.showDialog?.("info", api.translate("Game Store not Started"), {
                        text: api.translate("The game requires {{storeid}} to be running in parallel. " +
                            "Vortex will now attempt to start up the store for you.", { replace: { storeid: gameStoreId } }),
                    }, [
                        { label: "Cancel", action: () => reject(new UserCanceled()) },
                        { label: "Start Store", action: () => resolve() },
                    ]);
                }));
        };
        // Ask consent or start up the store directly.
        const startStore = () => askConsent
            ? askConsentDialog()
                .then(() => launchStore())
                .catch((err) => Bluebird.resolve())
            : launchStore();
        // Start up the store.
        return startStore();
    }
    identifyStore = toBlue(async (gamePath) => {
        const normalize = await getNormalizeFunc(gamePath);
        const fallback = async (store, gamePath) => {
            try {
                const gameInfo = (await store.allGames()).find((game) => normalize(game.gamePath) === normalize(gamePath));
                return gameInfo !== undefined;
            }
            catch (err) {
                return false;
            }
        };
        for (const store of this.getStores()) {
            if (store.identifyGame !== undefined) {
                if (await store.identifyGame?.(gamePath, (gamePath) => fallback(store, gamePath))) {
                    return store.id;
                }
            }
            else {
                if (await fallback(store, gamePath)) {
                    return store.id;
                }
            }
        }
        return undefined;
    });
    reloadGames(api) {
        if (!!api && !this.mApi) {
            this.mApi = api;
        }
        const stores = this.getStores().filter((store) => !!store);
        this.mApi?.sendNotification?.({
            id: "gamestore-reload",
            type: "activity",
            message: "Loading game stores...",
        });
        log("info", "reloading game store games", stores.map((store) => store.id).join(", "));
        return Bluebird.each(stores, (store) => store?.reloadGames !== undefined
            ? store.reloadGames().catch((err) => {
                // Game store was unable to reload its games
                //  we log this and jump to the next store.
                err["gameStore"] = store.id;
                log("error", "gamestore failed to reload its games", err);
                return Bluebird.resolve();
            })
            : Bluebird.resolve()).then(() => {
            this.mApi?.dismissNotification?.("gamestore-reload");
            return Bluebird.resolve();
        });
    }
    /**
     * @returns list of stores, sorted by priority
     */
    storeIds() {
        return this.mStores.sort((lhs, rhs) => (lhs.priority ?? defaultPriority) - (rhs.priority ?? defaultPriority));
    }
    isStoreRunning(storeExecPath) {
        const runningProcesses = winapi.GetProcessList();
        const exeId = makeExeId(storeExecPath);
        return (runningProcesses.find((runningProc) => exeId === runningProc.exeFile.toLowerCase()) !== undefined);
    }
    validInput(input) {
        return !input || (Array.isArray(input) && input.length === 0)
            ? false
            : true;
    }
    getStores() {
        if (this.mStores) {
            return this.mStores;
        }
        // It's possible that the game mode manager has yet
        //  to load the stores.
        try {
            this.mStores = getGameStores().filter((store) => !!store);
            this.mStoresDict = this.mStores.reduce((prev, store) => {
                prev[store.id] = store;
                return prev;
            }, {});
            return this.mStores;
        }
        catch (err) {
            log("debug", "stores have yet to load", err);
            return [];
        }
    }
    /**
     * Returns a store entry for a specified pattern.
     * @param searchType dictates which functor we execute.
     * @param pattern the pattern we're looking for.
     * @param storeId optional parameter used when trying to query a specific store.
     */
    findGameEntry(searchType, pattern, storeId) {
        const entryInfo = (entry) => searchType === "id" ? entry.appid : entry.name;
        const wrapNamePattern = (gameName) => {
            if (searchType !== "name") {
                // Not a name searchType.
                return gameName;
            }
            // We need to match the game name _exactly_ otherwise
            //  false positives could occur, for example:
            //  The Elder Scrolls V: Skyrim could potentially match
            //  The Elder Scrolls V: Skyrim Special Edition, in which
            //  case the game extension will look for TESV.exe and be unable
            //  to find it, failing discovery completely even though the user
            //  has Oldrim installed in a different location.
            return "^" + gameName + "$";
        };
        // For obvious reasons, this should only be used for
        //  name searchTypes; using this for id's would potentially
        // cause false positives.
        const rgxMatcher = Array.isArray(pattern)
            ? new RegExp(pattern.map(wrapNamePattern).join("|"))
            : new RegExp(wrapNamePattern(pattern));
        const matcher = Array.isArray(pattern)
            ? (entry) => pattern.indexOf(entryInfo(entry)) !== -1
            : (entry) => entryInfo(entry) === pattern;
        const name = Array.isArray(pattern) ? pattern.join(" - ") : pattern;
        const stores = this.mStores.map((store) => store.id).join(", ");
        // queriedStore object is only populated if the game store helper caller
        //  is looking for a specific game store.
        let queriedStore = undefined;
        if (storeId) {
            try {
                queriedStore = this.getGameStore(storeId);
            }
            catch (err) {
                // It's possible for a game store to be missing
                //  especially if it is added by a 3rd party extension.
                log("warn", "Game entry not found in specified store", {
                    pattern: name,
                    storeId,
                    availableStores: stores,
                });
                return Bluebird.reject(new GameEntryNotFound(name, stores));
            }
        }
        const gameStores = (queriedStore ? [queriedStore] : this.getStores()).filter((store) => !!store);
        if (gameStores === undefined || gameStores.length === 0) {
            const stores = gameStores !== undefined
                ? gameStores.map((store) => store.id).join(", ")
                : "";
            log("debug", "Game entry not found", {
                pattern: name,
                availableStores: stores,
            });
            return Bluebird.reject(new GameEntryNotFound(name, stores));
        }
        return Bluebird.reduce(gameStores, (accum, store) => store
            .allGames()
            .then((entries) => {
            const entry = searchType === "id"
                ? entries.find(matcher)
                : entries.find((ent) => rgxMatcher.test(ent.name));
            if (entry) {
                accum.push(entry);
            }
            return Bluebird.resolve(accum);
        })
            .catch(GameEntryNotFound, () => Bluebird.resolve(accum)), []).then((foundEntries) => {
            // TODO: A cool future feature here would be to allow the user to select
            //  the gamestore he wants to use. But for now, we just return the
            //  first instance we found.
            if (foundEntries.length > 0) {
                return Bluebird.resolve(foundEntries[0]);
            }
            else {
                log("debug", "Game entry not found", {
                    pattern: name,
                    availableStores: stores,
                });
                return Bluebird.reject(new GameEntryNotFound(name, stores));
            }
        });
    }
}
// const instance: GameStoreHelper = new GameStoreHelper();
const instance = new Proxy({}, {
    get(target, name) {
        if (target["inst"] === undefined) {
            target["inst"] = new GameStoreHelper();
        }
        return target["inst"][name];
    },
    set(target, name, value) {
        if (target["inst"] === undefined) {
            target["inst"] = new GameStoreHelper();
        }
        target["inst"][name] = value;
        return true;
    },
});
export default instance;
